import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, viewChild, ViewContainerRef, effect } from '@angular/core';
import { AttributeListColumnModel } from '../models/attribute-list-column.model';
import { Store } from '@ngrx/store';
import { PopoverService, OverlayRef, PopoverPositionEnum, BrowserHelper, I18nPaginatorIntl, TooltipDirective } from '@tailormap-viewer/shared';
import { Observable, of, switchMap, take, combineLatest } from 'rxjs';
import {
  selectDataForSelectedTab, selectHasNoRowsForSelectedTab, selectLoadingDataSelectedTab,
  selectPagingDataSelectedTab, selectSelectedTab,
} from '../state/attribute-list.selectors';
import { MatPaginatorIntl, PageEvent, MatPaginator } from '@angular/material/paginator';
import { AttributeListStateService } from '../services/attribute-list-state.service';
import { AttributeListDataService } from '../services/attribute-list-data.service';
import { AttributeListPagingDialogComponent } from '../attribute-list-paging-dialog/attribute-list-paging-dialog.component';
import { AttributeListPagingDataType } from '../models/attribute-list-paging-data.type';
import { SimpleAttributeFilterService } from '../../../filter/services/simple-attribute-filter.service';
import { BaseComponentTypeEnum } from '@tailormap-viewer/api';
import { AttributeListFeatureRegistrationService } from '../services/attribute-list-feature-registration.service';
import { MapService } from '@tailormap-viewer/map';
import { selectCQLFilters, selectViewerId } from '../../../state';
import { AttributeListManagerService } from '../services/attribute-list-manager.service';
import { map } from 'rxjs/operators';
import { MatToolbar } from '@angular/material/toolbar';
import { MatButton } from '@angular/material/button';
import { AttributeListExportButtonComponent } from '../attribute-list-export-button/attribute-list-export-button.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AsyncPipe } from '@angular/common';


@Component({
    selector: 'tm-attribute-list-tab-toolbar',
    templateUrl: './attribute-list-tab-toolbar.component.html',
    styleUrls: ['./attribute-list-tab-toolbar.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: MatPaginatorIntl, useClass: I18nPaginatorIntl },
    ],
    imports: [
        MatToolbar,
        MatButton,
        AttributeListExportButtonComponent,
        MatProgressSpinner,
        TooltipDirective,
        MatPaginator,
        AsyncPipe,
    ],
})
export class AttributeListTabToolbarComponent implements OnInit, OnDestroy {
  private store$ = inject(Store);
  private popoverService = inject(PopoverService);
  private attributeListStateService = inject(AttributeListStateService);
  private simpleAttributeFilterService = inject(SimpleAttributeFilterService);
  private attributeListFeatureRegistrationService = inject(AttributeListFeatureRegistrationService);
  private attributeListDataService = inject(AttributeListDataService);
  private mapService = inject(MapService);
  private attributeListApiService = inject(AttributeListManagerService);
  private attributeListFeaturesContainer = viewChild('attributeListFeaturesContainer', { read: ViewContainerRef });

  public columns: AttributeListColumnModel[] = [];

  private pagingPopover: OverlayRef | null = null;
  public loadingData$: Observable<boolean> = of(false);
  public pagingData$: Observable<AttributeListPagingDataType | null> = of(null);
  public hasFilters$: Observable<boolean> = of(false);
  public hasFiltersForOtherFeatureTypes$: Observable<boolean> = of(false);
  public showZoomToButton$ = combineLatest([
    this.store$.select(selectHasNoRowsForSelectedTab),
    this.store$.select(selectSelectedTab),
  ]).pipe(map(([ noRows, selectedTab ]) => {
    return !!selectedTab && this.attributeListApiService.canZoomToBounds(selectedTab.tabSourceId) && !selectedTab.loadingError && !noRows;
  }));

  constructor() {
    effect(() => {
      const components = this.attributeListFeatureRegistrationService.registeredAdditionalFeatures();
      const attributeListFeaturesContainer = this.attributeListFeaturesContainer();
      if (!attributeListFeaturesContainer) {
        return;
      }
      attributeListFeaturesContainer.clear();
      components.forEach(component => {
        attributeListFeaturesContainer.createComponent(component.component);
      });
    });
  }

  public ngOnInit() {
    this.loadingData$ = this.store$.select(selectLoadingDataSelectedTab);
    this.pagingData$ = this.store$.select(selectPagingDataSelectedTab);
    this.hasFilters$ = combineLatest([
      this.store$.select(selectSelectedTab),
      this.store$.select(selectDataForSelectedTab),
    ])
      .pipe(
        switchMap(([ tab, data ]) => {
          if (!tab?.layerId) {
            return of(false);
          }
          return this.simpleAttributeFilterService.hasFilter$(BaseComponentTypeEnum.ATTRIBUTE_LIST, tab.layerId, data?.featureType);
        }),
      );
    this.hasFiltersForOtherFeatureTypes$ = combineLatest([
      this.store$.select(selectSelectedTab),
      this.store$.select(selectDataForSelectedTab),
    ])
      .pipe(
        switchMap(([ tab, data ]) => {
          if (!tab?.layerId) {
            return of(false);
          }
          return this.simpleAttributeFilterService.hasFiltersForOtherFeatureTypes$(BaseComponentTypeEnum.ATTRIBUTE_LIST, tab.layerId, data?.featureType);
        }),
      );
  }

  public ngOnDestroy() {
    if (this.pagingPopover) {
      this.pagingPopover.close();
    }
  }

  public onPageChange($event: PageEvent): void {
    this.attributeListStateService.executeActionForCurrentData(dataId => {
      this.attributeListDataService.updatePage(dataId, $event.pageIndex + 1);
    });
  }

  public showJumpToPage($event: MouseEvent) {
    if (!$event.target || !($event.target instanceof HTMLElement)) {
      return;
    }
    if ($event.target.className.indexOf('paginator-range-label') !== -1) {
      if (this.pagingPopover && this.pagingPopover.isOpen) {
        this.pagingPopover.close();
        return;
      }
      const WINDOW_WIDTH = 150;
      this.pagingPopover = this.popoverService.open({
        origin: $event.target,
        content: AttributeListPagingDialogComponent,
        height: 80,
        width: Math.min(WINDOW_WIDTH, BrowserHelper.getScreenWith()),
        closeOnClickOutside: true,
        position: PopoverPositionEnum.BOTTOM_RIGHT_DOWN,
        positionOffset: 10,
      });
    }
  }

  public clearAllFilters() {
    this.store$.select(selectSelectedTab)
      .pipe(take(1))
      .subscribe(tab => {
        if (!tab?.layerId) {
          return;
        }
        this.simpleAttributeFilterService.removeAllFiltersForLayer(BaseComponentTypeEnum.ATTRIBUTE_LIST, tab.layerId);
      });
  }

  public clearFilters() {
    combineLatest([
      this.store$.select(selectSelectedTab),
      this.store$.select(selectDataForSelectedTab),
    ])
      .pipe(take(1))
      .subscribe(([ tab, data ]) => {
        if (!tab?.layerId) {
          return;
        }
        this.simpleAttributeFilterService.removeFiltersForLayer(BaseComponentTypeEnum.ATTRIBUTE_LIST, tab.layerId, data?.featureType);
      });
  }

  public zoomToDataBounds() {
    combineLatest([
      this.store$.select(selectSelectedTab),
      this.store$.select(selectViewerId),
      this.store$.select(selectCQLFilters),
    ])
      .pipe(
        take(1),
        switchMap(([ tab, applicationId, filters ]) => {
          if (!tab?.layerId || !applicationId) {
            return of(null);
          }
          const filter = filters.get(tab.layerId);
          return this.attributeListApiService.retrieveZoomToExtentBounds$(tab.tabSourceId, { applicationId, layerId: tab.layerId, filter });
      }))
      .subscribe(bounds => {
        if (!bounds) {
          return;
        }
        this.mapService.zoomToBounds(bounds);
      });
  }
}
